import React, { useState } from 'react';
import { TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import styled from 'styled-components';
import HealtheraTable from '../../../../../components/Table/Table';
import useDebouncedOrganisationSearch from '../../../CreateClinic/hooks/useOrganisationSearch';
import useListingOrganisations from '../../hooks/useListingOrganisations';
import { Organisation } from '../../../../../types/organisation';
import { ModifyClinicProps } from '../../../types';
import AddIcon from '@material-ui/icons/Add';
import TableContainer from '../../../../Shared/TableContainer';
import { FulfilmentTypeEnum } from '../../../../../apis/clinics/types';

interface ClinicListingOrganisationsTabProps {
  clinic: ModifyClinicProps['clinic'];
  fulfilmentType: FulfilmentTypeEnum;
}

const ClinicListingOrganisationsTab: React.FC<ClinicListingOrganisationsTabProps> = ({ clinic, fulfilmentType }) => {
  const { searchInput, setSearchInput, organisations } = useDebouncedOrganisationSearch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    organisations: listingOrganisations,
    totalCount,
    addOrganisation,
    removeOrganisation,
  } = useListingOrganisations(clinic._id, page, rowsPerPage);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organisationToDelete, setOrganisationToDelete] = useState<Organisation | null>(null);
  const [typeSwitchError, setTypeSwitchError] = useState<string | null>(null);

  const handleAddOrganisation = async (organisation: Organisation) => {
    await addOrganisation(organisation._id);
    setSearchInput('');
  };

  const handleRemoveOrganisation = async (organisationId: string) => {
    const clinicFulfilmentType = clinic.fulfilment_type || FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS;
    await removeOrganisation(organisationId, clinicFulfilmentType);
    setDeleteDialogOpen(false);
    setOrganisationToDelete(null);
  };

  const handleDeleteClick = (org: Organisation) => {
    setOrganisationToDelete(org);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setOrganisationToDelete(null);
    setTypeSwitchError(null);
  };

  const prepareSearchData = (orgs: Organisation[]) => {
    return [
      {
        view: {
          name: 'Name',
        },
        headers: true
      },
      ...orgs.map(org => ({
        view: {
          name: org.name,
        },
        button: {
          title: 'Add',
          onClick: () => handleAddOrganisation(org),
          icon: <AddIcon />
        }
      }))
    ];
  };

  const prepareListingData = (orgs: Organisation[]) => {
    return [
      {
        view: {
          name: 'Name',
        },
        headers: true
      },
      ...orgs.map(org => ({
        view: {
          name: org.name,
        },
        button: {
          title: 'Remove',
          onClick: () => handleDeleteClick(org),
          disabled: fulfilmentType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && listingOrganisations.length === 1
        }
      }))
    ];
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const EmptyStateContainer = styled.div`
    padding: 24px;
    text-align: center;
  `;

  const renderContent = () => {
    switch (fulfilmentType) {
      case FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS:
      case FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK:
      default:
        return (
          <>
            <SearchContainer>
              <SearchField
                fullWidth
                label="Search organisations to add"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </SearchContainer>

            {searchInput.length > 2 && (
              <HealtheraTable
                data={prepareSearchData(organisations)}
                displayPagination={false}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                onChangePage={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                totalCount={organisations.length}
                isLiveDashboard={false}
                noHover={true}
                onRowClick={() => {}}
              />
            )}

            <TableContainer>
              {listingOrganisations.length > 0 ? (
                <HealtheraTable
                  data={prepareListingData(listingOrganisations)}
                  displayPagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  onChangePage={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  totalCount={totalCount}
                  isLiveDashboard={false}
                  noHover={true}
                  onRowClick={() => {}}
                />
              ) : (
                <EmptyStateContainer>
                  <Typography variant="body1" color="textSecondary">
                    No listing organisations have been added yet. Search for an organisation above to add one
                  </Typography>
                </EmptyStateContainer>
              )}
            </TableContainer>
          </>
        );
    }
  };

  return (
    <>
      <Container>
        <h2>&nbsp;</h2>
        {renderContent()}
        {typeSwitchError && (
          <Typography color="error" style={{ marginTop: 8 }}>
            {typeSwitchError}
          </Typography>
        )}

        <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Remove Listing Organisation</DialogTitle>
          <DialogContent>
            Are you sure you want to remove {organisationToDelete?.name} as a listing organisation?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => organisationToDelete && handleRemoveOrganisation(organisationToDelete._id)}
              color="secondary"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 24px;
`;

const SearchContainer = styled.div`
  padding-bottom: 24px;
`;

const SearchField = styled(TextField)`
  max-width: 50%;
`;

export default ClinicListingOrganisationsTab;
